// 3rd party
import { Component, OnInit, OnDestroy, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/Rx';
import { TranslateService } from '@ngx-translate/core';
import { MatSidenav } from '@angular/material';
import * as FileSaver from 'file-saver';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';

// components
import { AppComponent } from 'app/app.component';
import { LayoutMainComponent } from 'app/shared/layout/layout-main/layout-main.component';

// helpers
import { Helper } from 'app/shared/helpers/helper';

// models
import { Calibration } from 'app/calibrations/shared/calibration.model';
import { StoredFile } from 'app/shared/models/stored-file.model';

// services
import { DialogsService } from 'app/shared/services/dialog/dialog.service';
import { StoredFileService } from 'app/shared/services/stored-file.service';
import { CalibrationService } from 'app/calibrations/shared/calibration.service';

@Component({
  selector: 'app-assay-list',
  templateUrl: './assay-list.component.html',
  styleUrls: ['./assay-list.component.css']
})
export class AssayListComponent implements OnInit, OnDestroy {

  @ViewChild('sidenavRight') sidenavRight: MatSidenav;
  @ViewChild('pdfViewer') pdfViewer: PdfJsViewerComponent;

  cancelSidenavOpen: boolean = false;
  checkedItems: number = 0;
  checkedMenuItems: string[] = [];
  chkAll: boolean;
  columns: any[] = [
    { tag: 's', name: 'CalibrationStatus', value: 'Translate.LaboratoryEvaluation', isChecked: true, isIconColumn: true, tooltipValue: 'Translate.LaboratoryEvaluation' },
    { tag: 'ad', name: 'CustomerStatus', value: 'Translate.CustomerEvaluation', isChecked: true, isIconColumn: true, tooltipValue: 'Translate.CustomerEvaluation' },
    { tag: 'att', name: 'HasAttachment', value: 'Translate.HasAttachments', isChecked: true, isIconColumn: true, tooltipValue: 'Translate.HasAttachments' },
    { tag: 'c', name: 'Code', value: 'Translate.Code', isChecked: true },
    { tag: 'd', name: 'Date', value: 'Translate.Date', isChecked: true },
    { tag: 'ico', name: 'Instrument.Code', value: 'Assays.Sample', isChecked: true },
    { tag: 'it', name: 'Instrument.InstrumentType.Name', value: 'Assays.Class', isChecked: true },
    { tag: 't', name: 'CalibrationType.Name', value: 'Assays.Type', isChecked: true },
    { tag: 'p', name: 'IsField', value: 'Translate.Local', isChecked: true },
    { tag: 'os', name: 'OrderService.Code', value: 'Translate.OrderService', isChecked: false },
  ];
  currentPage: number = 1;
  data: any[] = [];
  filter: string;
  isCheckingAll: boolean = false;
  isLoading: boolean = false;
  isSearching: boolean = false;
  months: any[];
  orderBy: string = 'code';
  overallCount: number = 1;
  pageSize: number = 25;
  qtVisibleColumns: number = this.columns.filter(c => c.isChecked).length;
  search: string = '';
  selectedItems: any = [];
  selectedMonth = new Date().getMonth() + 1;
  selectedSituation = -1;
  selectedYear = new Date().getFullYear();
  situations: any[];
  sortOrder: string = 'asc';
  subscribe: Subscription;
  totalPages: number = 1;
  years: any[];

  constructor(
    private app: AppComponent,
    private calibrationService: CalibrationService,
    public dialog: DialogsService,
    private el: ElementRef,
    public mainLayout: LayoutMainComponent,
    private renderer2: Renderer2,
    private router: Router,
    public storedFileService: StoredFileService,
    public translate: TranslateService
  ) { }

  ngOnInit() {
    this.refresh();
    this.fillFilters();
    this.checkColumnsPreferences();
  }

  ngOnDestroy() {
    if (this.subscribe)
      this.subscribe.unsubscribe();
  }

  checkAll(ev) {
    this.isCheckingAll = true;
    this.data.forEach((item) => {
      item.checked = ev.source.checked;
    });
    this.isCheckingAll = false;
    this.checkedItems = this.data.filter(item => item.checked).length;
  }

  checkColumn(event, str: string) {
    event.stopPropagation();

    let idx = this.columns.findIndex(c => c.tag === str);
    if (idx === -1) {
      return;
    }

    if (this.columns.filter(c => c.isChecked).length === 1 && this.columns[idx].isChecked) {
      return;
    }

    this.columns[idx].isChecked = !this.columns[idx].isChecked;
    this.qtVisibleColumns = this.columns.filter(c => c.isChecked).length;

    this.saveColumnsPreferences(this.columns);
  }

  checkColumnsPreferences() {
    if (!localStorage.getItem('webview.assayList.columns')) {
      this.saveColumnsPreferences(this.columns);
    }
    const savedColumns = JSON.parse(localStorage.getItem('webview.assayList.columns'));

    this.columns.forEach(c => c.isChecked = savedColumns.findIndex(sc => sc === c.tag) !== -1);
    this.qtVisibleColumns = this.columns.filter(c => c.isChecked).length;
  }

  closeSidenav() {
    this.sidenavRight.close();
    this.router.navigate([`/${this.app.currentContact.Domain}/assays/`]);
  }

  downloadCal() {
    this.isLoading = true;
    let selectedAssays = this.data.filter(d => d.checked) as Calibration[];
    let params = selectedAssays.map(calibration => {
      return {
        identifier: calibration.Identifier,
        tableName: 'new_calibrations'
      };
    });

    this.calibrationService.download(params)
      .subscribe(data => {
        this.isLoading = false;
        if (data) {
          FileSaver.saveAs(data, selectedAssays.length > 1 ? 'ensaios.cal' : `${selectedAssays[0].Code}.cal`);
        } else {
          this.dialog.error('Message.ErrorAlert', this.checkedItems > 1 ? 'Message.DownloadAssaysError' : 'Message.DownloadAssayError');
        }
      }, () => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', this.checkedItems > 1 ? 'Message.DownloadAssaysError' : 'Message.DownloadAssayError');
      });
  }

  enableSearch() {
    this.isSearching = !this.isSearching;
    this.search = '';
  }

  fillFilters() {
    this.fillSituationFilter();
    this.fillMonthFilter();
    this.fillYearFilter();
  }

  fillMonthFilter() {
    this.months = new Array(
      { id: -1, name: 'Configurations.FilterAll' },
      { id: 1, name: 'Months.January' },
      { id: 2, name: 'Months.February' },
      { id: 3, name: 'Months.March' },
      { id: 4, name: 'Months.April' },
      { id: 5, name: 'Months.May' },
      { id: 6, name: 'Months.June' },
      { id: 7, name: 'Months.July' },
      { id: 8, name: 'Months.August' },
      { id: 9, name: 'Months.September' },
      { id: 10, name: 'Months.October' },
      { id: 11, name: 'Months.November' },
      { id: 12, name: 'Months.December' },
    );
    this.selectedMonth = new Date().getMonth() + 1;
  }

  fillSituationFilter() {
    this.situations = new Array(
      { id: -1, name: 'Configurations.FilterAll' },
      { id: 0, name: 'Translate.None' },
      { id: 2, name: 'Translate.Disapproved' },
      { id: 1, name: 'Translate.Approved' },
      { id: 3, name: 'Translate.ApprovedWithReservation' },
    );
    this.selectedSituation = -1;
  }

  fillYearFilter() {
    this.years = new Array({ id: -1, name: 'Configurations.FilterAll' });
    let currentYear = new Date().getFullYear();
    for (let i = 2013; i <= currentYear; i++) {
      this.years.push({ id: i, name: i.toString() });
    }
    this.selectedYear = currentYear;
  }

  filterValueChanged() {
    this.refresh(this.search);
  }

  getSituationDescription(status: number) {
    return this.calibrationService.getSituationDescription(status);
  }

  getSituationIcon(status: number) {
    return this.calibrationService.getSituationIcon(status);
  }

  getStatusIcon(status: number) {
    return this.calibrationService.getStatusIcon(status);
  }

  isColumnChecked(str: string): boolean {
    let idx = this.columns.findIndex(c => c.tag === str);
    if (idx === -1) {
      return false;
    }

    return this.columns[idx].isChecked;
  }

  nextPage(e) {
    this.currentPage = e;
    this.refresh();
  }

  onSelectionChange() {
    if (!this.isCheckingAll) {
      if (this.data.filter(item => item.checked).length !== this.data.length && this.chkAll) {
        this.chkAll = false;
      }
      if (this.data.filter(item => item.checked).length === this.data.length && !this.chkAll) {
        this.chkAll = true;
      }

      this.selectedItems = this.data ? this.data.filter(d => d.checked) : [];
      this.checkedItems = this.selectedItems.length;
    }
  }

  openCustomerStatusDialog(readOnly: boolean) {
    let lst: any[] = [];
    lst = this.data.filter(d => d.checked);
    this.dialog.customerStatus(lst, readOnly).subscribe((res) => {
      if (res === undefined && !readOnly) {
        this.refresh(this.search, undefined, undefined);
      }
    });
  }

  openDetails(item: any) {
    if (this.cancelSidenavOpen) {
      this.cancelSidenavOpen = false;
      return;
    }

    this.router.navigate([`/${this.app.currentContact.Domain}/assays/` + item.Identifier]);
    this.sidenavRight.open();
  }

  prevent($event) {
    this.cancelSidenavOpen = true;
    $event.preventDefault();
  }

  previousPage(e) {
    this.currentPage = e;
    this.refresh();
  }

  refresh(search?: string, orderBy?: string, sortOrder?: string) {
    this.filter = '';
    this.selectedItems = this.data ? this.data.filter(d => d.checked) : [];
    if (search !== undefined) {
      this.search = search;
    }
    if (orderBy) {
      this.orderBy = orderBy;
    }
    if (sortOrder) {
      this.sortOrder = sortOrder;
    }
    let y = new Date().getFullYear();
    let dInitial = new Date(2013, 0, 1);
    let dFinal = new Date(y, 11, 31);
    if (this.selectedMonth !== undefined && this.selectedMonth !== -1 && !this.mainLayout.isMobile) {
      dInitial.setMonth(this.selectedMonth - 1);
      dFinal.setMonth(this.selectedMonth - 1);
      this.filter += (this.filter ? ',' : '') + '"Date_BETWEEN \'' + dInitial.toISOString().split('T')[0] + '\' AND \'' + dFinal.toISOString().split('T')[0] + '\'"';
    }
    if (this.selectedYear !== undefined && this.selectedYear !== -1 && !this.mainLayout.isMobile) {
      dInitial.setFullYear(this.selectedYear);
      dFinal.setFullYear(this.selectedYear);
      this.filter += (this.filter ? ',' : '') + '"Date_BETWEEN \'' + dInitial.toISOString().split('T')[0] + '\' AND \'' + dFinal.toISOString().split('T')[0] + '\'"';
    }
    if (this.selectedSituation !== undefined && this.selectedSituation !== -1 && !this.mainLayout.isMobile) {
      this.filter += (this.filter ? ',' : '') + '"CustomerStatus_=' + this.selectedSituation + '"';
    }

    if (this.app.currentContact !== undefined && this.app.currentContact.Customer !== undefined) {
      this.filter += (this.filter ? ', ' : '') + '"Customer.Identifier_=\'' + this.app.currentContact.Customer.Identifier + '\'"';
    } else {
      this.filter += (this.filter ? ', ' : '') + '"Customer.Identifier_=\'ERROR\'"';
    }
    this.filter += (this.filter ? ', ' : '') + '"Status_=1"';
    const lstSpinner = this.el.nativeElement.querySelectorAll('mat-spinner');
    for (let x = 0; x < lstSpinner.length; x++) {
      this.renderer2.setStyle(lstSpinner[x], 'display', 'block');
    }

    this.filter += (this.filter ? ', ' : '') + '"IsAssay_=1"';
    let fields = '"' + this.columns.map(c => c.name).join('","') + '"';
    fields += ',"CustomerApproveDate","CustomerStatusDescription","ApproveContact.Name"';
    this.subscribe = this.calibrationService.get(null, this.search, this.orderBy, this.sortOrder, this.pageSize, this.currentPage, this.filter, fields).subscribe(data => {
      for (let x = 0; x < lstSpinner.length; x++) {
        this.renderer2.setStyle(lstSpinner[x], 'display', 'none');
      }

      this.data = Helper.deserialize(data.text());

      if (this.selectedItems) {
        this.data.forEach(d => d.checked = this.selectedItems.find(s => s.Identifier === d.Identifier && s.checked) ? true : false);
      }
      this.checkedItems = this.data.filter(d => d.checked).length;
      this.overallCount = this.data.length > 0 ? this.data[0].OverallCount : 0;
      this.totalPages = Helper.getTotalPages(this.pageSize, this.overallCount);
    });
  }

  saveColumnsPreferences(columns: any[]) {
    localStorage.setItem('webview.assayList.columns', JSON.stringify(columns.filter(c => c.isChecked).map(c2 => c2.tag)));
  }

  searchData() {
    this.refresh(this.search);
  }

  setCustomerStatus() {
    this.openCustomerStatusDialog(false);
  }

  setPageSize(n) {
    this.pageSize = n;
    this.currentPage = 1;
    this.refresh();
  }

  sortData(data) {
    this.refresh(undefined, data.orderBy, data.sortOrder);
  }

  view(download: boolean) {
    this.isLoading = true;
    let identifiers = this.data.filter(d => d.checked).map(obj => obj.Identifier);
    let lstFilters = '"Calibration.Identifier_in(\'' + identifiers.join('\',\'') + '\')"';
    let lstFields = '"SystemFileName","Calibration.Id","Calibration.Code","Calibration.Date"';
    let storedFiles: any[] = [];

    this.storedFileService.get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(value => {
        storedFiles = value.json() as StoredFile[];
        if (!storedFiles || storedFiles.length === 0) {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewAssayError', 'info_outline', '#009688');
          this.isLoading = false;
        }
        this.storedFileService.download(storedFiles)
          .subscribe(data => {
            this.isLoading = false;
            if (data) {
              const file = new Blob([data], { type: data.type });

              this.storedFileService.view(file, download, this.pdfViewer, `Ensaio ${storedFiles[0].Calibration.Code}`);

            } else {
              this.dialog.error('Message.ErrorAlert', 'Message.ViewAssayError');
              this.isLoading = false;
            }
          }, () => {
            this.dialog.error('Message.ErrorAlert', 'Message.OpenAssayError');
            this.isLoading = false;
          });
      }, () => {
        this.dialog.error('Message.ErrorAlert', 'Message.OpenAssayError');
        this.isLoading = false;
      });
  }

  viewCustomerStatus() {
    this.openCustomerStatusDialog(true);
  }
}



// WEBPACK FOOTER //
// ./src/app/assays/assay-list/assay-list.component.ts